/**
 * Brand theme generation from two colours (a strong primary and a pastel secondary).
 *
 * Everything is computed in OKLCH so lightness steps look even across hues. The
 * output is a flat map of CSS custom properties holding "L C H" triplets, which
 * the web app consumes as `oklch(var(--x) / <alpha-value>)` and e-mails / PDFs
 * consume via `oklchToHex`.
 */

/** Mély málnapiros (lilás piros) és telt ibolya – a platform (propsys) alapszínei. */
export const DEFAULT_PRIMARY = "#C0265A";
export const DEFAULT_SECONDARY = "#6D28D9";

export type Oklch = { l: number; c: number; h: number };

const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type ScaleStep = (typeof SCALE_STEPS)[number];

/** Target lightness per step. 500 is replaced by the input colour's own lightness. */
const LIGHTNESS: Record<ScaleStep, number> = {
  50: 0.975,
  100: 0.945,
  200: 0.89,
  300: 0.82,
  400: 0.74,
  500: 0.66,
  600: 0.57,
  700: 0.48,
  800: 0.39,
  900: 0.31,
  950: 0.23,
};

/** Chroma multiplier per step: pale and very dark steps carry less colour. */
const CHROMA: Record<ScaleStep, number> = {
  50: 0.12,
  100: 0.22,
  200: 0.42,
  300: 0.65,
  400: 0.88,
  500: 1,
  600: 0.95,
  700: 0.85,
  800: 0.72,
  900: 0.6,
  950: 0.5,
};

export function isHexColor(value: string): boolean {
  return /^#([0-9a-f]{6})$/i.test(value);
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`Invalid hex colour: ${hex}`);
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255) as [number, number, number];
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const fromLinear = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.sqrt(A * A + B * B);
  let H = (Math.atan2(B, A) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { l: L, c: C, h: H };
}

function oklchToLinearRgb({ l, c, h }: Oklch): [number, number, number] {
  const hr = (h * Math.PI) / 180;
  const a = c * Math.cos(hr);
  const b = c * Math.sin(hr);
  const l_ = Math.pow(l + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m_ = Math.pow(l - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s_ = Math.pow(l - 0.0894841775 * a - 1.291485548 * b, 3);
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

function inGamut(color: Oklch): boolean {
  return oklchToLinearRgb(color).every((v) => v >= -0.0001 && v <= 1.0001);
}

/** Reduces chroma until the colour fits in sRGB (binary search). */
export function clampToGamut(color: Oklch): Oklch {
  if (inGamut(color)) return color;
  let lo = 0;
  let hi = color.c;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut({ ...color, c: mid })) lo = mid;
    else hi = mid;
  }
  return { ...color, c: lo };
}

export function oklchToHex(color: Oklch): string {
  const rgb = oklchToLinearRgb(clampToGamut(color)).map((v) => {
    const s = Math.round(Math.min(1, Math.max(0, fromLinear(v))) * 255);
    return s.toString(16).padStart(2, "0");
  });
  return `#${rgb.join("")}`;
}

/** WCAG relative luminance, used for choosing readable text on a colour. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * 11 lépcsős skála egy színből. `minChroma`: a pasztell kísérőszínnél a középső és sötét
 * lépcsők így élénk árnyalatot kapnak ugyanabban a hue-ban (különben szürkék lennének).
 * Az 500-as lépcső a bemenet világosságát követi, de a skála monoton marad.
 */
export function buildScale(hex: string, minChroma = 0.02): Record<ScaleStep, Oklch> {
  const base = hexToOklch(hex);
  const baseChroma = Math.max(base.c, minChroma);
  const l500 = Math.min(0.72, Math.max(0.6, base.l));
  const out = {} as Record<ScaleStep, Oklch>;
  for (const step of SCALE_STEPS) {
    const l = step === 500 ? l500 : LIGHTNESS[step];
    out[step] = clampToGamut({ l, c: baseChroma * CHROMA[step], h: base.h });
  }
  return out;
}

const SOFT_MIN_CHROMA = 0.15;

/** Fehér vagy sötét szöveg a színen: fehér, ha legalább 3:1 a kontraszt (vastag gombfelirat). */
function textOn(hex: string): "white" | "ink" {
  return contrastRatio(hex, "#ffffff") >= 3 ? "white" : "ink";
}

const triplet = ({ l, c, h }: Oklch) => `${(l * 100).toFixed(2)}% ${c.toFixed(4)} ${h.toFixed(2)}`;

const INK = { l: 0.24, c: 0.02, h: 285 } satisfies Oklch;
const WHITE = { l: 1, c: 0, h: 0 } satisfies Oklch;

export type ThemeVars = Record<string, string>;

/**
 * CSS variables for the given brand colours. Keys include the leading `--`.
 * - `--brand-{step}` and `--soft-{step}`: full scales of primary / secondary
 * - semantic tokens (`--primary`, `--primary-foreground`, `--accent`, `--ring` ...)
 */
export function buildThemeVars(primaryHex?: string | null, secondaryHex?: string | null): ThemeVars {
  const primary = primaryHex && isHexColor(primaryHex) ? primaryHex : DEFAULT_PRIMARY;
  const secondary = secondaryHex && isHexColor(secondaryHex) ? secondaryHex : DEFAULT_SECONDARY;

  const brand = buildScale(primary);
  const soft = buildScale(secondary, SOFT_MIN_CHROMA);
  const vars: ThemeVars = {};
  for (const step of SCALE_STEPS) {
    vars[`--brand-${step}`] = triplet(brand[step]);
    vars[`--soft-${step}`] = triplet(soft[step]);
  }

  // A gomb színe maga a bemenet; a felirat fehér, ha elég kontrasztos, különben sötét.
  vars["--primary"] = triplet(hexToOklch(primary));
  vars["--primary-foreground"] = triplet(textOn(primary) === "white" ? WHITE : INK);
  // Links and text accents need to be readable on white: pick the first dark-enough step.
  const readable = ([600, 700, 800] as const).find((s) => contrastRatio(oklchToHex(brand[s]), "#ffffff") >= 4.5) ?? 800;
  vars["--primary-text"] = triplet(brand[readable]);
  vars["--ring"] = triplet(brand[400]);
  vars["--accent"] = triplet(soft[100]);
  vars["--accent-foreground"] = triplet(soft[800]);
  vars["--secondary"] = triplet(hexToOklch(secondary));
  vars["--secondary-foreground"] = triplet(soft[900]);
  // Jellegzetes gradiens: a fő szín → a pasztell élénk változata.
  vars["--gradient-from"] = triplet(hexToOklch(primary));
  vars["--gradient-to"] = triplet(soft[500]);
  return vars;
}

/** Hex values for e-mails and PDFs, where CSS variables / oklch are not available. */
export function buildEmailPalette(primaryHex?: string | null, secondaryHex?: string | null) {
  const primary = primaryHex && isHexColor(primaryHex) ? primaryHex : DEFAULT_PRIMARY;
  const secondary = secondaryHex && isHexColor(secondaryHex) ? secondaryHex : DEFAULT_SECONDARY;
  const brand = buildScale(primary);
  const soft = buildScale(secondary, SOFT_MIN_CHROMA);
  const onPrimary = textOn(primary) === "white" ? "#ffffff" : oklchToHex(INK);
  const readable = ([600, 700, 800] as const).find((s) => contrastRatio(oklchToHex(brand[s]), "#ffffff") >= 4.5) ?? 800;
  return {
    primary,
    onPrimary,
    primaryText: oklchToHex(brand[readable]),
    background: oklchToHex(soft[50]),
    accent: oklchToHex(soft[100]),
    gradientFrom: primary,
    gradientTo: oklchToHex(soft[500]),
    ink: oklchToHex(INK),
  };
}
